import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { PencomRouter } from "@/router";
import { PostHogProvider } from "posthog-js/react";
import { env } from "@/utils/env";

import { RouterProvider } from "react-router-dom";
import AuthListener from "./router/AuthListener";
import MaintenancePage from "@/components/maintenance/MaintenancePage";
import MaintenanceLoading from "@/components/maintenance/MaintenanceLoading";
import {
  consumeEmployerBypassFromUrl,
  isQaBypassActive,
} from "@/utils/qaBypass";
import { useMaintenanceStore } from "@/stores/maintenanceStore";
import { useAuthStore } from "@/stores/authStore";

consumeEmployerBypassFromUrl();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

const MaintenanceGate = () => {
  const isMaintenance = useMaintenanceStore((s) => s.isMaintenance);
  const isInitializing = useMaintenanceStore((s) => s.isInitializing);
  const message = useMaintenanceStore((s) => s.message);
  const startPolling = useMaintenanceStore((s) => s.startPolling);
  const stopPolling = useMaintenanceStore((s) => s.stopPolling);

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  useEffect(() => {
    if (!isMaintenance) return;
    const { isAuthenticated, logout } = useAuthStore.getState();
    if (isAuthenticated) logout(true);
  }, [isMaintenance]);

  if (isInitializing) return <MaintenanceLoading />;

  // Employers with the bypass token in sessionStorage skip the maintenance page.
  if (isMaintenance && !isQaBypassActive())
    return <MaintenancePage message={message ?? ""} />;

  return (
    <>
      <RouterProvider router={PencomRouter} />
      <AuthListener />
    </>
  );
};

function App() {
  return (
    <PostHogProvider
      apiKey={env.POSTHOG_KEY}
      options={{ api_host: env.POSTHOG_HOST }}
    >
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <MaintenanceGate />
          <Toaster position="top-right" richColors closeButton />
        </QueryClientProvider>
      </HelmetProvider>
    </PostHogProvider>
  );
}

export default App;
